/* eslint-disable prettier/prettier, curly */
/**
 * Message View - Full message details with reply/forward actions
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeStyles, Button, Text, Badge, Spinner, Avatar } from '@fluentui/react-components';
import {
  ArrowReplyRegular,
  ArrowReplyAllRegular,
  ArrowForwardRegular,
  DeleteRegular,
  ArchiveRegular,
  AttachRegular,
  ArrowDownloadRegular,
  ChevronLeftRegular,
} from '@fluentui/react-icons';
import { ApiClient, newToast, fullDate } from '../../../../configurations/index.config';
import { getErrorMessage } from '../../../../types';
import { formatFileSize, isAttachmentExpired, getTimeUntilExpiry } from '../../../../utils/memoUtils';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#ffffff',
  },
  header: {
    padding: '16px 24px',
    borderBottom: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginLeft: 'auto',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  messageHeader: {
    marginBottom: '24px',
  },
  subject: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  metaLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#605e5c',
    minWidth: '60px',
  },
  metaValue: {
    fontSize: '14px',
    color: '#323130',
  },
  recipientBadge: {
    marginRight: '8px',
  },
  messageBody: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#323130',
    marginBottom: '24px',
  },
  attachmentsSection: {
    borderTop: '1px solid #e1dfdd',
    paddingTop: '16px',
  },
  attachmentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
  },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    border: '1px solid #e1dfdd',
    borderRadius: '4px',
    backgroundColor: '#fafafa',
    gap: '12px',
  },
  attachmentExpired: {
    opacity: 0.6,
    backgroundColor: '#fff4ce',
  },
  attachmentInfo: {
    flex: 1,
    minWidth: 0,
  },
  attachmentName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
  },
  attachmentMeta: {
    fontSize: '12px',
    color: '#605e5c',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
  warningBanner: {
    padding: '12px 16px',
    backgroundColor: '#fff4ce',
    border: '1px solid #f7e06e',
    borderRadius: '4px',
    marginBottom: '16px',
  },
});

interface Attachment {
  id: number;
  original_filename: string;
  file_size: number;
  mime_type: string;
  uploaded_at: string;
  expires_at: string;
  is_expired: boolean;
  is_deleted: boolean;
}

interface Recipient {
  mailbox_id: number;
  alias: string;
  name: string;
  delivery_status: string;
  is_read: boolean;
  read_at: string | null;
}

interface Message {
  message_id: number;
  thread_id: string;
  subject: string;
  body: string;
  message_type: string;
  sender: {
    mailbox_id: number;
    alias: string;
    name: string;
    department: string;
  };
  recipients: {
    to: Recipient[];
    cc: Recipient[];
    bcc: Recipient[];
  };
  attachments: Attachment[];
  sent_at: string;
  is_read: boolean;
  read_at: string | null;
  folder: string;
  is_starred: boolean;
}

export const MessageViewFluentUI: React.FC = () => {
  const styles = useStyles();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [message, setMessage] = useState<Message | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadMessage(parseInt(id));
    }
  }, [id]);

  const loadMessage = async (messageId: number) => {
    try {
      setLoading(true);
      const response = await ApiClient.get(`/memo/message/${messageId}`);

      if (response.data.status === 'success') {
        setMessage(response.data.data);
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
      navigate('/account/memo/inbox');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = () => {
    navigate(`/account/memo/compose?reply=${message?.message_id}`);
  };

  const handleReplyAll = () => {
    navigate(`/account/memo/compose?replyall=${message?.message_id}`);
  };

  const handleForward = () => {
    navigate(`/account/memo/compose?forward=${message?.message_id}`);
  };

  const handleArchive = async () => {
    if (!message) return;

    try {
      const response = await ApiClient.post('/memo/archive', {
        message_ids: [message.message_id],
      });

      if (response.data.status === 'success') {
        newToast('success', 'Message archived');
        navigate('/account/memo/inbox');
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleTrash = async () => {
    if (!message) return;

    try {
      const response = await ApiClient.post('/memo/trash', {
        message_ids: [message.message_id],
      });

      if (response.data.status === 'success') {
        newToast('success', 'Message moved to trash');
        navigate('/account/memo/inbox');
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleDownloadAttachment = async (attachmentId: number, filename: string) => {
    try {
      const response = await ApiClient.get(`/memo/attachment/${attachmentId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      newToast('success', 'Attachment downloaded');
    } catch (error) {
      newToast('error', getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading message..." />
      </div>
    );
  }

  if (!message) {
    return (
      <div className={styles.loadingContainer}>
        <Text>Message not found</Text>
      </div>
    );
  }

  const hasExpiredAttachments = message.attachments.some(att => isAttachmentExpired(att.expires_at) || att.is_deleted);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button appearance="subtle" icon={<ChevronLeftRegular />} onClick={() => navigate(-1)}>
          Back
        </Button>

        <div className={styles.actions}>
          <Button appearance="subtle" icon={<ArrowReplyRegular />} onClick={handleReply}>Reply</Button>
          <Button appearance="subtle" icon={<ArrowReplyAllRegular />} onClick={handleReplyAll}>Reply All</Button>
          <Button appearance="subtle" icon={<ArrowForwardRegular />} onClick={handleForward}>Forward</Button>
          <Button appearance="subtle" icon={<ArchiveRegular />} onClick={handleArchive}>Archive</Button>
          <Button appearance="subtle" icon={<DeleteRegular />} onClick={handleTrash}>Delete</Button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.messageHeader}>
          <Text className={styles.subject}>{message.subject}</Text>

          <div className={styles.metaRow}>
            <Avatar name={message.sender.name} size={32} />
            <div style={{ flex: 1 }}>
              <Text weight="semibold" block>{message.sender.name}</Text>
              <Text size={200} style={{ color: '#605e5c' }}>{message.sender.alias}</Text>
            </div>
            <Text size={200} style={{ color: '#605e5c' }}>{fullDate(message.sent_at)}</Text>
          </div>

          {message.recipients.to.length > 0 && (
            <div className={styles.metaRow} style={{ marginTop: '12px' }}>
              <Text className={styles.metaLabel}>To:</Text>
              <div className={styles.metaValue}>
                {message.recipients.to.map((recipient, idx) => (
                  <Badge key={idx} className={styles.recipientBadge} appearance="outline">
                    {recipient.name} ({recipient.alias})
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {message.recipients.cc.length > 0 && (
            <div className={styles.metaRow}>
              <Text className={styles.metaLabel}>Cc:</Text>
              <div className={styles.metaValue}>
                {message.recipients.cc.map((recipient, idx) => (
                  <Badge key={idx} className={styles.recipientBadge} appearance="outline">
                    {recipient.name} ({recipient.alias})
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.messageBody} dangerouslySetInnerHTML={{ __html: message.body }} />

        {message.attachments.length > 0 && (
          <div className={styles.attachmentsSection}>
            {hasExpiredAttachments && (
              <div className={styles.warningBanner}>
                <Text size={300}>⚠️ Some attachments have expired (72-hour retention policy)</Text>
              </div>
            )}

            <Text weight="semibold">
              <AttachRegular style={{ marginRight: '8px' }} />
              Attachments ({message.attachments.length})
            </Text>

            <div className={styles.attachmentsList}>
              {message.attachments.map(attachment => {
                const expired = isAttachmentExpired(attachment.expires_at) || attachment.is_deleted;
                return (
                  <div key={attachment.id} className={`${styles.attachmentItem} ${expired ? styles.attachmentExpired : ''}`}>
                    <AttachRegular style={{ fontSize: '20px' }} />
                    <div className={styles.attachmentInfo}>
                      <Text className={styles.attachmentName}>{attachment.original_filename}</Text>
                      <Text className={styles.attachmentMeta}>
                        {formatFileSize(attachment.file_size)} • {attachment.mime_type}
                        {!expired && ` • Expires: ${getTimeUntilExpiry(attachment.expires_at)}`}
                        {expired && ' • Expired'}
                      </Text>
                    </div>
                    {!expired && (
                      <Button appearance="subtle" icon={<ArrowDownloadRegular />} onClick={() => handleDownloadAttachment(attachment.id, attachment.original_filename)}>
                        Download
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageViewFluentUI;
